/**
 * Unit type definitions and constants.
 * All unit stats are configurable from this central location.
 * Architecture supports future database storage and retrieval.
 */

/**
 * Import dev settings store.
 * This is now safe because we've broken the circular dependency:
 * - units.ts (settings) no longer imports from constants/units.ts (hardcoded values)
 * - devSettingsStore.ts imports from units.ts (settings)
 * - constants/units.ts imports from devSettingsStore.ts
 * No cycle exists!
 */
import { useDevSettingsStore } from '../stores/devSettingsStore';

/**
 * Unit type enum.
 */
export enum UnitType {
  BUILDER = 'builder',
  MELEE = 'melee',
  RANGED = 'ranged',
  ENEMY_MELEE = 'enemy_melee',
}

/**
 * Unit faction enum.
 */
export enum UnitFaction {
  PLAYER = 'player',
  ENEMY = 'enemy',
}

/**
 * Unit stats interface.
 */
export interface UnitStats {
  /** Maximum health points */
  health: number;
  /** Armor value (reduces incoming damage) */
  armor: number;
  /** Sight range in tiles (radius) */
  sightRange: number;
  /** Attack range in tiles */
  attackRange: number;
  /** Base damage per attack */
  baseDamage: number;
  /** Time between attacks in seconds */
  attackSpeed: number;
  /** Movement speed in tiles per second */
  movementSpeed: number;
}

/**
 * Unit definition with stats and display name.
 */
export interface UnitDefinition {
  type: UnitType;
  name: string;
  faction: UnitFaction;
  stats: UnitStats;
}

/**
 * Unit definitions map.
 * All unit stats are defined here for easy configuration.
 */
export const UNIT_DEFINITIONS: Record<UnitType, UnitDefinition> = {
  [UnitType.BUILDER]: {
    type: UnitType.BUILDER,
    name: 'Builder',
    faction: UnitFaction.PLAYER,
    stats: {
      health: 50,
      armor: 0,
      sightRange: 7,
      attackRange: 1,
      baseDamage: 5,
      attackSpeed: 1.25,
      movementSpeed: 2,
    },
  },
  [UnitType.MELEE]: {
    type: UnitType.MELEE,
    name: 'Swordsman',
    faction: UnitFaction.PLAYER,
    stats: {
      health: 100,
      armor: 5,
      sightRange: 5,
      attackRange: 1,
      baseDamage: 25,
      attackSpeed: 1,
      movementSpeed: 1,
    },
  },
  [UnitType.RANGED]: {
    type: UnitType.RANGED,
    name: 'Archer',
    faction: UnitFaction.PLAYER,
    stats: {
      health: 75,
      armor: 3,
      sightRange: 10,
      attackRange: 5,
      baseDamage: 15,
      attackSpeed: 0.75,
      movementSpeed: 1.5,
    },
  },
  [UnitType.ENEMY_MELEE]: {
    type: UnitType.ENEMY_MELEE,
    name: 'Enemy Melee',
    faction: UnitFaction.ENEMY,
    stats: {
      health: 200,
      armor: 1,
      sightRange: 5,
      attackRange: 1,
      baseDamage: 10,
      attackSpeed: 1,
      movementSpeed: 1.25,
    },
  },
};

/**
 * Get unit stats by unit type.
 * Checks dev settings first, then falls back to UNIT_DEFINITIONS.
 */
export function getUnitStats(unitType: UnitType): UnitStats {
  // Try to get dev settings
  try {
    const storeState = useDevSettingsStore.getState();
    const unitSettings = storeState?.settings?.units;

    if (unitSettings) {
      // Map unit type to dev settings keys
      const unitKeyMap: Record<UnitType, string> = {
        [UnitType.BUILDER]: 'builder',
        [UnitType.MELEE]: 'melee',
        [UnitType.RANGED]: 'ranged',
        [UnitType.ENEMY_MELEE]: 'enemyMelee',
      };

      const unitKey = unitKeyMap[unitType];
      if (unitKey) {
        // Get stat keys for this unit type
        const healthKey = `${unitKey}Health` as keyof typeof unitSettings;
        const armorKey = `${unitKey}Armor` as keyof typeof unitSettings;
        const sightRangeKey = `${unitKey}SightRange` as keyof typeof unitSettings;
        const attackRangeKey = `${unitKey}AttackRange` as keyof typeof unitSettings;
        const baseDamageKey = `${unitKey}BaseDamage` as keyof typeof unitSettings;
        const attackSpeedKey = `${unitKey}AttackSpeed` as keyof typeof unitSettings;
        const movementSpeedKey = `${unitKey}MovementSpeed` as keyof typeof unitSettings;

        // Read values from unitSettings - they should always exist after merge with defaults
        // Since DEFAULT_UNIT_CONFIG has all values set, and merge ensures they're present,
        // we can safely read them directly
        const stats: UnitStats = {
          health: (unitSettings[healthKey] as number) ?? UNIT_DEFINITIONS[unitType].stats.health,
          armor: (unitSettings[armorKey] as number) ?? UNIT_DEFINITIONS[unitType].stats.armor,
          sightRange:
            (unitSettings[sightRangeKey] as number) ?? UNIT_DEFINITIONS[unitType].stats.sightRange,
          attackRange:
            (unitSettings[attackRangeKey] as number) ??
            UNIT_DEFINITIONS[unitType].stats.attackRange,
          baseDamage:
            (unitSettings[baseDamageKey] as number) ?? UNIT_DEFINITIONS[unitType].stats.baseDamage,
          attackSpeed:
            (unitSettings[attackSpeedKey] as number) ??
            UNIT_DEFINITIONS[unitType].stats.attackSpeed,
          movementSpeed:
            (unitSettings[movementSpeedKey] as number) ??
            UNIT_DEFINITIONS[unitType].stats.movementSpeed,
        };

        return stats;
      }
    }
  } catch (error) {
    // Dev settings not available (e.g., in tests or production), fall through to defaults
  }

  // Fall back to UNIT_DEFINITIONS
  return UNIT_DEFINITIONS[unitType].stats;
}

/**
 * Get unit definition by unit type.
 */
export function getUnitDefinition(unitType: UnitType): UnitDefinition {
  return UNIT_DEFINITIONS[unitType];
}

/**
 * Check if a unit type is a player unit.
 */
export function isPlayerUnit(unitType: UnitType): boolean {
  return UNIT_DEFINITIONS[unitType].faction === UnitFaction.PLAYER;
}

/**
 * Check if a unit type is an enemy unit.
 */
export function isEnemyUnit(unitType: UnitType): boolean {
  return UNIT_DEFINITIONS[unitType].faction === UnitFaction.ENEMY;
}
